import { useEffect, useMemo, useState } from "react";

import { Search } from "../../../../../components/design-system";
import { COPY } from "../../copy";
import {
  formatCustomerCurrency,
  formatCustomerDate,
  getAttributedOrder,
  getCustomerContinuedAdSales,
  hasContinuedAttribution,
  type AcquiredCustomerRow,
  type CustomerType,
} from "../../customersData";
import { fillCopy, renderCopy } from "../../renderCopy";
import {
  useCardPaddingClass,
  useIsCompact,
  useTableRowHeightClass,
} from "../CardDensity";
import { InsightsFilterMenu } from "../InsightsFilterMenu";
import { InsightsSectionTitle } from "../InsightsSectionTitle";
import { NotApplicableCell } from "../NotApplicableCell";
import { DottedLabel } from "../overview/DottedLabel";
import { HeaderTooltip } from "../overview/HeaderTooltip";
import { TablePagination } from "../TablePagination";

/** Matches the mock's density beside the metrics card above it. */
const PAGE_SIZE = 6;

const TABLE_COPY = COPY.cohort.v2.table;

/** The column definitions this table shares with the Customers tab. */
const COLUMN_COPY = COPY.shared.columns;

/** Sentinel for the unfiltered state, so the filter's value is always a string. */
const ALL_AD_TYPES = "all";

/**
 * Singular, matching the Customers tab: a row is one retailer's one conversion,
 * so it names the ad that drove it rather than the campaign type.
 */
const AD_TYPE_LABEL: Record<CustomerType, string> = {
  New: "New customer ad",
  Returning: "Reorder ad",
};

/**
 * The unfiltered option collapses to "Ad type: All" on the trigger, so the chip
 * stays short while the menu still spells the option out.
 */
const AD_TYPE_FILTER_OPTIONS = [
  { value: ALL_AD_TYPES, label: "All ad types", triggerLabel: "All" },
  { value: "New", label: AD_TYPE_LABEL.New },
  { value: "Returning", label: AD_TYPE_LABEL.Returning },
];

/** Marks the column the rows are ordered by. */
function SortDescIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      className="shrink-0 text-[#333]"
      aria-hidden
    >
      <path d="M5 8L2 3H8L5 8Z" fill="currentColor" />
    </svg>
  );
}

/**
 * Whole-number % lift of a customer's first-year reorders over the order the ad
 * drove. Null when there is no attributed lifetime to cut apart or the lift
 * isn't positive, so the tag only appears for customers who reordered.
 */
function continuedLiftPct(row: AcquiredCustomerRow): number | null {
  const continued = getCustomerContinuedAdSales(row);
  if (continued == null || row.firstMonthAdSales <= 0) {
    return null;
  }
  const pct = Math.round((continued / row.firstMonthAdSales) * 100);
  return pct > 0 ? pct : null;
}

function LiftTag({ pct }: { pct: number }) {
  return (
    <span className="inline-flex items-center rounded bg-[#eef1ff] px-1.5 py-0.5 text-xs leading-4 font-medium text-[#3b3f8f]">
      +{pct}%
    </span>
  );
}

function LiftTooltipContent({
  row,
  pct,
}: {
  row: AcquiredCustomerRow;
  pct: number;
}) {
  return (
    <>
      {renderCopy(
        COPY.cohort.table.liftTag,
        {
          amount: formatCustomerCurrency(getCustomerContinuedAdSales(row) ?? 0),
          pct,
        },
        "font-semibold"
      )}
    </>
  );
}

/**
 * The underline lives on the value rather than the cell, so the em dash standing
 * in for a missing number doesn't get dressed up as a link.
 */
function ValueCell({
  value,
  underline = false,
}: {
  value: string | null;
  underline?: boolean;
}) {
  return (
    <span
      className={`${value == null ? "text-[#757575]" : "text-[#333]"} ${
        underline && value != null ? "underline" : ""
      }`}
    >
      {value ?? "—"}
    </span>
  );
}

/**
 * Sort key for the Lifetime sales column. Reorder rows carry no lifetime, and
 * sorting them as zero would interleave them with genuinely small cohorts, so
 * they're pushed below every row that has a figure.
 */
function lifetimeSortValue(row: AcquiredCustomerRow): number {
  if (!hasContinuedAttribution(row) || row.lifetimeSales == null) {
    return -1;
  }
  return row.lifetimeSales;
}

export function CohortConversionsTable({
  rows,
  month,
}: {
  rows: AcquiredCustomerRow[];
  /** Short cohort label, for the tooltips that name the month. */
  month: string;
}) {
  const [query, setQuery] = useState("");
  const [adType, setAdType] = useState<string>(ALL_AD_TYPES);
  const [page, setPage] = useState(1);
  const cardPaddingClass = useCardPaddingClass();
  const rowHeightClass = useTableRowHeightClass();
  // Matches the Customers tab's conversions table, which is this table on the
  // other page: V3 right-aligns the money and count columns.
  const figureAlignClass = useIsCompact() ? "text-right" : "";

  const visibleRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows
      .filter((row) => {
        if (adType !== ALL_AD_TYPES && row.customerType !== adType) {
          return false;
        }
        if (needle && !row.storeName.toLowerCase().includes(needle)) {
          return false;
        }
        return true;
      })
      .sort(
        (left, right) => lifetimeSortValue(right) - lifetimeSortValue(left)
      );
  }, [rows, query, adType]);

  // Filtering changes which rows are in the first six, so the page number would
  // otherwise point at a set that no longer exists.
  useEffect(() => {
    setPage(1);
  }, [query, adType, rows]);

  const totalPages = Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = visibleRows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  return (
    <div
      className={`bg-fs-surface-primary rounded-lg border border-[#dfe0e1] ${cardPaddingClass}`}
    >
      {/* The tooltip is shared with v1's table rather than duplicated into the
          v2 block: both list the same conversions, and v1 already names the
          month in that sentence. */}
      <h3>
        <InsightsSectionTitle
          tooltip={fillCopy(COPY.cohort.table.headingTooltip, { month })}
        >
          {fillCopy(TABLE_COPY.heading, { month })}
        </InsightsSectionTitle>
      </h3>

      <div className="mt-4 mb-4 flex flex-col gap-2 min-[733px]:flex-row min-[733px]:items-center">
        <div className="w-full min-[733px]:w-[348px]">
          <Search
            placeholder={TABLE_COPY.searchPlaceholder}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onClear={() => setQuery("")}
          />
        </div>
        <div className="w-full min-[733px]:w-auto">
          <InsightsFilterMenu
            name="cohort-conversions-ad-type"
            labelPrefix="Ad type"
            menuTitle="Filter by ad type"
            value={adType}
            options={AD_TYPE_FILTER_OPTIONS}
            onChange={setAdType}
            clearValue={ALL_AD_TYPES}
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[1200px] table-fixed text-left text-sm">
          {/* Pinned widths, so paging doesn't reshuffle the columns — same
              reason as the Customers tab's copy of this table, and the same
              trade: the figure columns get what their headers need, and the
              store name, store type, and location columns give it up and clip.
              Lifetime sales is widest because it carries the lift tag beside
              its figure. */}
          <colgroup>
            <col style={{ width: "12%" }} />
            <col style={{ width: "10.5%" }} />
            <col style={{ width: "9.5%" }} />
            {/* "New customer ad" doesn't wrap, so this column is sized to it
                rather than to its own header. */}
            <col style={{ width: "12%" }} />
            <col style={{ width: "10.5%" }} />
            <col style={{ width: "10.5%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "13%" }} />
          </colgroup>
          <thead>
            <tr className="border-b border-[#dfe0e1]">
              <th className="faire-label-medium px-4 py-3 whitespace-nowrap text-[#333]">
                Store name
              </th>
              <th className="faire-label-medium px-4 py-3 whitespace-nowrap text-[#333]">
                <HeaderTooltip tooltip={COLUMN_COPY.storeType}>
                  <DottedLabel>Store type</DottedLabel>
                </HeaderTooltip>
              </th>
              <th className="faire-label-medium px-4 py-3 whitespace-nowrap text-[#333]">
                Location
              </th>
              <th className="faire-label-medium px-4 py-3 whitespace-nowrap text-[#333]">
                Ad type
              </th>
              <th className="faire-label-medium px-4 py-3 whitespace-nowrap text-[#333]">
                Ad click date
              </th>
              <th className="faire-label-medium px-4 py-3 whitespace-nowrap text-[#333]">
                <HeaderTooltip tooltip={COLUMN_COPY.orderDate}>
                  <DottedLabel>Order date</DottedLabel>
                </HeaderTooltip>
              </th>
              <th
                className={`faire-label-medium px-4 py-3 whitespace-nowrap text-[#333] ${figureAlignClass}`}
              >
                <HeaderTooltip
                  tooltip={fillCopy(TABLE_COPY.orderAmount, { month })}
                >
                  <DottedLabel>Order amount</DottedLabel>
                </HeaderTooltip>
              </th>
              <th
                className={`faire-label-medium px-4 py-3 whitespace-nowrap text-[#333] ${figureAlignClass}`}
              >
                <HeaderTooltip tooltip={COLUMN_COPY.lifetimeOrders}>
                  <DottedLabel>Lifetime orders</DottedLabel>
                </HeaderTooltip>
              </th>
              <th
                className={`faire-label-medium px-4 py-3 whitespace-nowrap text-[#333] ${figureAlignClass}`}
                aria-sort="descending"
              >
                <span className="inline-flex items-center gap-1">
                  <HeaderTooltip tooltip={COLUMN_COPY.lifetimeSales}>
                    <DottedLabel>Lifetime sales</DottedLabel>
                  </HeaderTooltip>
                  <SortDescIcon />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => {
              // Reorder rows stop at the reorder, so the lifetime columns have
              // nothing to report for them.
              const continuedApplies = hasContinuedAttribution(row);
              const liftPct = continuedLiftPct(row);
              const order = getAttributedOrder(row);

              return (
                <tr
                  key={row.id}
                  className={`border-b border-[#dfe0e1] last:border-0 hover:bg-[#fafafa] ${rowHeightClass}`}
                >
                  {/* Long store names, store types, and locations clip to one
                      line rather than wrapping and forcing the row taller; the
                      title attribute keeps the full value reachable on hover. */}
                  <td className="px-4 py-4 text-sm leading-5 text-[#333]">
                    <span
                      className="block max-w-[180px] truncate underline"
                      title={row.storeName}
                    >
                      {row.storeName}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm leading-5 text-[#333]">
                    <span
                      className="block max-w-[140px] truncate"
                      title={row.storeType}
                    >
                      {row.storeType}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm leading-5 text-[#333]">
                    <span
                      className="block max-w-[140px] truncate"
                      title={row.location}
                    >
                      {row.location}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm leading-5 whitespace-nowrap text-[#333]">
                    {AD_TYPE_LABEL[row.customerType]}
                  </td>
                  <td className="px-4 py-4 text-sm leading-5 whitespace-nowrap text-[#333]">
                    {formatCustomerDate(row.adClickDate)}
                  </td>
                  <td className="px-4 py-4 text-sm leading-5 whitespace-nowrap">
                    <ValueCell
                      underline
                      value={formatCustomerDate(order.date)}
                    />
                  </td>
                  <td
                    className={`px-4 py-4 text-sm leading-5 whitespace-nowrap text-[#333] ${figureAlignClass}`}
                  >
                    {formatCustomerCurrency(order.amount)}
                  </td>
                  <td
                    className={`px-4 py-4 text-sm leading-5 ${figureAlignClass}`}
                  >
                    {continuedApplies ? (
                      <ValueCell
                        underline
                        value={
                          row.lifetimeOrders != null
                            ? String(row.lifetimeOrders)
                            : null
                        }
                      />
                    ) : (
                      <NotApplicableCell />
                    )}
                  </td>
                  <td
                    className={`px-4 py-4 text-sm leading-5 ${figureAlignClass}`}
                  >
                    {continuedApplies ? (
                      <span className="inline-flex items-center gap-2">
                        <ValueCell
                          value={
                            row.lifetimeSales != null
                              ? formatCustomerCurrency(row.lifetimeSales)
                              : null
                          }
                        />
                        {liftPct != null ? (
                          <HeaderTooltip
                            tooltip={
                              <LiftTooltipContent row={row} pct={liftPct} />
                            }
                          >
                            <LiftTag pct={liftPct} />
                          </HeaderTooltip>
                        ) : null}
                      </span>
                    ) : (
                      <NotApplicableCell />
                    )}
                  </td>
                </tr>
              );
            })}
            {pageRows.length === 0 ? (
              <tr>
                <td
                  colSpan={9}
                  className="px-4 py-10 text-center text-sm text-[#757575]"
                >
                  {TABLE_COPY.empty}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {totalPages > 1 ? (
        <div className="mt-4 flex justify-center">
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onClick={setPage}
          />
        </div>
      ) : null}
    </div>
  );
}
